import type {
  DataFetchParams,
  DataFetchResponse,
  DynamicFilterOptionsPage,
  FilterOptionsParams,
} from '@/components/DataTable';
import {
  getAccountAgentCommandFilterOptions,
  getOrganizationAgentCommandFilterOptions,
  listAccountAggregatedPackages,
  listAccountAggregatedRepositories,
  listAccountAgentCommandEvents,
  listOrganizationAggregatedPackages,
  listOrganizationAggregatedRepositories,
  listOrganizationAgentCommandEvents,
} from '@kirin/api-client/api/agent-command-events';
import type {
  AggregatedPackage,
  AggregatedPackageSortField,
  AggregatedRepository,
  AggregatedRepositorySortField,
  AgentCommandEvent,
  AgentCommandEventSortField,
  AgentCommandEventType,
  AgentCommandFilterOptionField,
} from '@kirin/api-client/contracts/agent-command-events';
import {
  FilterOperators,
  type FilterValue,
} from '@kirin/api-client/contracts/table';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { apiClient } from '@/lib/api-client';

/** Extra (non-DataTable) filter state the page layers on top of the table. */
export interface AgentCommandExtraFilters {
  from?: string;
}

/**
 * Fetch the aggregated Packages view for the DataTable (KIR-3807). Default sort
 * is last-seen descending. Org-scoped when `organizationId` is set, else the
 * personal-account API.
 */
export async function fetchAggregatedPackages(
  organizationId: string | undefined,
  extra: AgentCommandExtraFilters,
  params: DataFetchParams,
  _signal?: AbortSignal
): Promise<DataFetchResponse<AggregatedPackage>> {
  const query = {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    sortBy:
      (params.sort?.field as AggregatedPackageSortField | undefined) ??
      'lastSeen',
    sortOrder: params.sort?.order ?? SortOrder.Desc,
    search: params.search,
    filters: params.filters,
    from: extra.from,
  };

  const response = organizationId
    ? await listOrganizationAggregatedPackages(apiClient, organizationId, query)
    : await listAccountAggregatedPackages(apiClient, query);

  return {
    data: response.packages,
    pagination: response.pagination,
    meta: { activeFilters: [] },
  };
}

/** Query key for the aggregated Packages DataTable (org or personal). */
export function aggregatedPackagesQueryKey(
  organizationId: string | undefined,
  extra: AgentCommandExtraFilters,
  params: DataFetchParams
): readonly unknown[] {
  return [
    'agent-command-events',
    organizationId ?? 'account',
    'aggregated-packages',
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      sortBy: params.sort?.field,
      sortOrder: params.sort?.order,
      search: params.search,
      filters: params.filters,
      from: extra.from,
    },
  ];
}

/**
 * Fetch the aggregated Repositories view for the DataTable (KIR-3807). Default
 * sort last-seen desc. Org-scoped when `organizationId` is set, else personal.
 */
export async function fetchAggregatedRepositories(
  organizationId: string | undefined,
  extra: AgentCommandExtraFilters,
  params: DataFetchParams,
  _signal?: AbortSignal
): Promise<DataFetchResponse<AggregatedRepository>> {
  const query = {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    sortBy:
      (params.sort?.field as AggregatedRepositorySortField | undefined) ??
      'lastSeen',
    sortOrder: params.sort?.order ?? SortOrder.Desc,
    search: params.search,
    filters: params.filters,
    from: extra.from,
  };
  const response = organizationId
    ? await listOrganizationAggregatedRepositories(
        apiClient,
        organizationId,
        query
      )
    : await listAccountAggregatedRepositories(apiClient, query);
  return {
    data: response.repositories,
    pagination: response.pagination,
    meta: { activeFilters: [] },
  };
}

/** Query key for the aggregated Repositories DataTable (org or personal). */
export function aggregatedRepositoriesQueryKey(
  organizationId: string | undefined,
  extra: AgentCommandExtraFilters,
  params: DataFetchParams
): readonly unknown[] {
  return [
    'agent-command-events',
    organizationId ?? 'account',
    'aggregated-repositories',
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      sortBy: params.sort?.field,
      sortOrder: params.sort?.order,
      search: params.search,
      filters: params.filters,
      from: extra.from,
    },
  ];
}

// Pin one group-key column. A null key is a legitimate group (e.g. an
// unpinned `packageVersion`), so pin it with IS_EMPTY rather than dropping the
// filter — dropping would leave the column unconstrained and over-match every
// value (PR #2158 review, F2).
const eqFilter = (field: string, value: string | null): FilterValue =>
  value == null
    ? { field, operator: FilterOperators.IS_EMPTY, value: [] }
    : { field, operator: FilterOperators.INCLUDES, value: [value] };

/**
 * Filters pinning the flat command-events list to a single aggregated package
 * group. Every group-key column is pinned (null keys via IS_EMPTY) so the
 * drill-down matches exactly that group.
 */
export function buildPackageGroupFilters(pkg: {
  packageName: string | null;
  packageVersion: string | null;
  packageManager: string | null;
  sourceUrl: string | null;
  projectKey: string | null;
}): FilterValue[] {
  return [
    eqFilter('packageName', pkg.packageName),
    eqFilter('packageVersion', pkg.packageVersion),
    eqFilter('packageManager', pkg.packageManager),
    eqFilter('sourceUrl', pkg.sourceUrl),
    // Pin the unique project identity, not the display name (Bugbot B1).
    eqFilter('projectKey', pkg.projectKey),
  ];
}

/** Filters pinning the flat list to a single repository group (null keys via IS_EMPTY). */
export function buildRepositoryGroupFilters(repo: {
  repoUrl: string | null;
  action: string | null;
  branch: string | null;
  projectKey: string | null;
}): FilterValue[] {
  return [
    eqFilter('repoUrl', repo.repoUrl),
    eqFilter('action', repo.action),
    eqFilter('branch', repo.branch),
    eqFilter('projectKey', repo.projectKey),
  ];
}

/**
 * The unaggregated command events for one group (package or repo), most-recent
 * first. Backs both the occurrences drill-down (full page) and the detail
 * modal's 10-most-recent "raw commands" (small `limit`).
 */
export async function fetchGroupEvents(
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  filters: FilterValue[],
  options: { page?: number; limit: number }
): Promise<{ events: AgentCommandEvent[]; total: number }> {
  const query = {
    eventType,
    page: options.page ?? 1,
    limit: options.limit,
    sortBy: 'timestamp' as AgentCommandEventSortField,
    sortOrder: SortOrder.Desc,
    filters,
  };
  const response = organizationId
    ? await listOrganizationAgentCommandEvents(apiClient, organizationId, query)
    : await listAccountAgentCommandEvents(apiClient, query);
  return { events: response.events, total: response.pagination.total };
}

/** Query key for one group's unaggregated events (filters identify the group). */
export function groupEventsQueryKey(
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  filters: FilterValue[],
  options: { page?: number; limit: number }
): readonly unknown[] {
  return [
    'agent-command-events',
    organizationId ?? 'account',
    'group-events',
    eventType,
    {
      filters,
      page: options.page ?? 1,
      limit: options.limit,
    },
  ];
}

/**
 * Distinct-value options for a column's multi-select dropdown (org or personal).
 * One endpoint serves every filterable column via the `field` param.
 */
export async function fetchAgentCommandEventFilterOptions(
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  field: AgentCommandFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const query = {
    eventType,
    field,
    search: params.search,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
  };
  const response = organizationId
    ? await getOrganizationAgentCommandFilterOptions(
        apiClient,
        organizationId,
        query
      )
    : await getAccountAgentCommandFilterOptions(apiClient, query);
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for a column's distinct-value dropdown. */
export function agentCommandFilterOptionsQueryKey(
  organizationId: string | undefined,
  eventType: AgentCommandEventType,
  field: AgentCommandFilterOptionField,
  params: FilterOptionsParams
): readonly unknown[] {
  return [
    'agent-command-events',
    organizationId ?? 'account',
    'filter-options',
    eventType,
    field,
    { search: params.search, limit: params.limit, offset: params.offset },
  ];
}
